// AdjustPawner.jsx — zoom-safe, matches Renewpawner look & feel
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./PawnerForm.css"; // use the same compact/zoom-safe styling

const MOBILE_RE_8 = /^\d{8}$/;

const cleanGoldNumber = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  const cleaned = String(value).replace(/,/g, "").trim();
  return cleaned || "0";
};

const buildPawnGoldPayload = (pledgeNo, details) => {
  if (!pledgeNo || !details?.weights) return null;

  const weights = details.weights || {};
  const prices = details.prices || {};

  return {
    pledgeNo,

    gold1Qty: cleanGoldNumber(weights["999"]),
    gold2Qty: cleanGoldNumber(weights["24K"]),
    gold3Qty: cleanGoldNumber(weights["23K"]),
    gold4Qty: cleanGoldNumber(weights["22K"]),
    gold5Qty: cleanGoldNumber(weights["20K"]),
    gold6Qty: cleanGoldNumber(weights["18K"]),
    gold7Qty: cleanGoldNumber(weights["14K"]),
    gold8Qty: cleanGoldNumber(weights["9K"]),
    gold9Qty: cleanGoldNumber(weights["STONE"]),

    gold1Price: cleanGoldNumber(prices["999"]),
    gold2Price: cleanGoldNumber(prices["24K"]),
    gold3Price: cleanGoldNumber(prices["23K"]),
    gold4Price: cleanGoldNumber(prices["22K"]),
    gold5Price: cleanGoldNumber(prices["20K"]),
    gold6Price: cleanGoldNumber(prices["18K"]),
    gold7Price: cleanGoldNumber(prices["14K"]),
    gold8Price: cleanGoldNumber(prices["9K"]),
    gold9Price: cleanGoldNumber(prices["STONE"]),

    goldCode: prices.goldCode ?? details.goldCode ?? null,
    valAddAmt: cleanGoldNumber(details.valAdd),
  };
};

const savePawnGoldBreakdown = async ({ lookupPledgeNo, finalPledgeNo, transactionData }) => {
  const details = transactionData?.goldDetails;

  // No gold modal data was loaded/edited, so do not risk saving stale data.
  if (!details?.weights) return;

  const lookup = (lookupPledgeNo || finalPledgeNo || "").trim();
  const finalNo = (finalPledgeNo || lookupPledgeNo || "").trim();
  const payload = buildPawnGoldPayload(finalNo, details);

  if (!lookup || !payload) return;

  await axios.put("https://www.lianfoong.com/api/pawn-gold/pledge", payload, {
    params: { pledgeNo: lookup },
    withCredentials: true,
  });
};

export default function AdjustPawner() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasFetched = useRef(false);

  // from previous screen
  // NOTE: originalReceiptNo is used to look up the existing row even if receiptNo is being changed
  const { transactionData, receiptNo, originalReceiptNo } = location.state || {};

  // form state
  const [nric, setNric] = useState("");
  const [name, setName] = useState("");
  const [race, setRace] = useState("");
  const [sex, setSex] = useState("");
  const [nationality, setNationality] = useState("");
  const [sgAddr, setSgAddr] = useState("");
  const [dob, setDob] = useState(""); // yyyy-MM-dd
  const [contactNoMobile, setContactNoMobile] = useState("");
  const [blacklisted, setBlacklisted] = useState(false);

  const toTitle = (s) => (s || "").toString().toUpperCase();

  // Esc → Dashboard
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && navigate("/dashboard");
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // Prefill by receipt (once) — use originalReceiptNo if present, else fallback to receiptNo
  useEffect(() => {
    const rcptForPrefill = originalReceiptNo || receiptNo;
    if (!rcptForPrefill || hasFetched.current) return;
    hasFetched.current = true;
    axios
      .get("https://www.lianfoong.com/api/pawn-transactions/getPawnerDetails", {
        params: { oldReceiptNo: rcptForPrefill },
      })
      .then(({ data }) => hydrateFromCustomer(data))
      .catch((err) => console.error("Error fetching pawner by receipt:", err));
  }, [originalReceiptNo, receiptNo]);

  // Populate fields from customer payload
  const hydrateFromCustomer = (data, fallbackIc) => {
    setNric(data?.nirc || fallbackIc || "");
    setName(toTitle(data?.name));
    setRace(toTitle(data?.race));
    setSex(toTitle(data?.sex));
    setNationality(toTitle(data?.nationality));
    setSgAddr(toTitle(data?.sgAddr));
    setContactNoMobile(data?.contactNoMobile || "");
    setBlacklisted(Boolean(data?.blacklisted));
    const rawDob = data?.dob || "";
    setDob(
      rawDob
        ? (/^\d{4}-\d{2}-\d{2}$/.test(rawDob)
            ? rawDob
            : new Date(rawDob).toISOString().slice(0, 10))
        : ""
    );
  };

  // Lookup by NRIC (no format validation)
  const loadByNric = async (id) => {
    const ic = (id || "").trim().toUpperCase();
    if (!ic) return;
    try {
      const { data } = await axios.get(
        "https://www.lianfoong.com/api/customers/by-nric",
        { params: { nirc: ic } }
      );
      hydrateFromCustomer(data, ic);
    } catch {
      try {
        const { data } = await axios.post(
          "https://www.lianfoong.com/api/customers/nric",
          { nirc: ic }
        );
        hydrateFromCustomer(data, ic);
      } catch {
        // not found → keep typed NRIC, clear the rest
        setNric(ic);
        setName("");
        setRace("");
        setSex("");
        setNationality("");
        setSgAddr("");
        setDob("");
        setContactNoMobile("");
        setBlacklisted(false);
      }
    }
  };

  const onNricKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      loadByNric(nric);
    }
  };

  // simple checks (no NRIC validation)
  const validate = () => {
    if (contactNoMobile && !MOBILE_RE_8.test(contactNoMobile)) {
      alert("Mobile must be 8 digits (SG).");
      return false;
    }
    return true;
  };

  const sanitizeDob = (d) => {
    const s = (d || "").trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const iso = new Date(s);
    return isNaN(+iso) ? null : iso.toISOString().slice(0, 10);
  };

  const downloadReceipt = async (rcptNo, alsoPrint = false) => {
    if (!rcptNo) {
      alert("Missing receipt number.");
      return;
    }
    const res = await axios.get(
      "https://www.lianfoong.com/api/pawn-transactions/print",
      { params: { receiptNo: rcptNo, alsoPrint }, responseType: "blob" }
    );
    let filename = "receipt.pdf";
    const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"];
    if (cd && cd.includes("filename=")) {
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m?.[1]) filename = m[1];
    }
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const handler = prompt("Please enter your handler code:");
    if (!handler) {
      alert("Handler code is required!");
      return;
    }
    try {
      // upsert customer
      await axios.post("https://www.lianfoong.com/api/customers", {
        nirc: nric,
        name,
        race,
        sex,
        nationality,
        sgAddr,
        dob: sanitizeDob(dob),
        contactNoMobile,
        blacklisted,
      });

      // --- Update pawn-transaction ---
      // Use ORIGINAL receipt to locate the row; body may carry a NEW receiptNo/oldReceiptNo
      const lookupReceiptNo = originalReceiptNo || receiptNo || "";
      await axios.put(
        "https://www.lianfoong.com/api/pawn-transactions/update",
        { ...transactionData, pawnerNirc: nric, handler },
        { params: { receiptNo: lookupReceiptNo } }
      );

      // Use the possibly CHANGED receipt no for pawn-gold + printing
      const printReceiptNo = transactionData?.receiptNo || lookupReceiptNo;

      // Save pawn-gold breakdown.
      // lookupReceiptNo finds the old tbl_pawn_gold row.
      // printReceiptNo saves it under the final/new receipt number if the receipt was edited.
      try {
        await savePawnGoldBreakdown({
          lookupPledgeNo: lookupReceiptNo,
          finalPledgeNo: printReceiptNo,
          transactionData,
        });
      } catch (e) {
        console.error("pawn-gold update failed:", e);
      }

      // then continue to print/download as you already do...
      await downloadReceipt(printReceiptNo, false);
      alert("Ticket updated and receipt downloaded.");
      navigate("/dashboard");
    } catch (e) {
      console.error(e);
      alert("Failed to update ticket or download receipt.");
    }
  };

  return (
    <div className="pf-page">
      <div className="pawner-form pawner-form--wide pf-compact">
        {/* Header */}
        <div className="pf-header">
          <h2 className="pf-title">Pawner Record (Adjust)</h2>
        </div>

        {/* Body — wide, no internal scrolling */}
        <div className="pf-body-noscroll">
          <div className="pf-grid-12">
            <div className="pf-field col-4">
              <label className="pf-label">NRIC No</label>
              <input
                className="pf-input"
                value={nric}
                onChange={(e) => setNric(e.target.value.toUpperCase())}
                onKeyDown={onNricKeyDown}
                onBlur={() => loadByNric(nric)}
              />
            </div>

            <div className="pf-field col-8">
              <label className="pf-label">Name</label>
              <input
                className="pf-input"
                value={name}
                onChange={(e) => setName(toTitle(e.target.value))}
              />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Race</label>
              <input
                className="pf-input"
                value={race}
                onChange={(e) => setRace(toTitle(e.target.value))}
              />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Sex</label>
              <input
                className="pf-input"
                value={sex}
                onChange={(e) => setSex(toTitle(e.target.value))}
              />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Nationality</label>
              <input
                className="pf-input"
                value={nationality}
                onChange={(e) => setNationality(toTitle(e.target.value))}
              />
            </div>

            <div className="pf-field col-12">
              <label className="pf-label">Address (Singapore)</label>
              <textarea
                className="pf-input pf-textarea"
                value={sgAddr}
                onChange={(e) => setSgAddr(toTitle(e.target.value))}
              />
            </div>

            <div className="pf-field col-6">
              <label className="pf-label">Birthdate</label>
              <input
                type="date"
                className="pf-input"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
              />
            </div>

            <div className="pf-field col-6">
              <label className="pf-label">Contact No (Mobile)</label>
              <input
                className="pf-input"
                value={contactNoMobile}
                onChange={(e) => setContactNoMobile(e.target.value)}
              />
            </div>

            <div className="pf-field col-4">
              <label className="pf-label">Blacklisted</label>
              <input
                type="checkbox"
                className="pf-checkbox"
                checked={blacklisted}
                onChange={(e) => setBlacklisted(e.target.checked)}
              />
            </div>
          </div>
        </div>

        {/* Footer — non-sticky */}
        <div className="pf-footer-nosticky">
          <button type="button" className="pf-btn pf-btn--ghost" onClick={() => navigate("/dashboard")}>
            Back
          </button>
          <button type="button" className="pf-btn pf-btn--danger" onClick={() => navigate("/dashboard")}>
            Cancel
          </button>
          <button type="button" className="pf-btn pf-btn--primary" onClick={handleSubmit}>
            Update &amp; Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}
